import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { View, StatusBar, Platform } from 'react-native';
import Animated, { withTiming, useSharedValue, useDerivedValue, useAnimatedProps, useAnimatedScrollHandler, } from 'react-native-reanimated';
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context';
import { AppView } from 'design-system';
import { useAppState } from 'hooks';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { extractKeyFromKey } from 'utils/flatListHelpers';
import LearnVideo from '../../settings/learn/components/LearnVideo';
const viewabilityConfig = {
    waitForInteraction: false,
    viewAreaCoveragePercentThreshold: 90,
};
const noop = () => { };
const selectSlicedVideos = (videos, startIndex) => {
    if (videos?.length) {
        return videos.slice(startIndex, videos.length);
    }
    return [];
};
const InvestLearn = ({ route, navigation }) => {
    const styles = useStyles(styleSet);
    const isGestureActive = useSharedValue(0);
    const scrollEnabled = useSharedValue(true);
    const { top, bottom } = useSafeAreaInsets();
    const { width } = useSafeAreaFrame();
    const isGestureActiveBool = useDerivedValue(() => isGestureActive.value !== 0, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: false,
        });
    }, []);
    const memoizedStateCallbacks = useMemo(() => ({
        appStateDidChange: (state) => {
            if (state === 'active') {
                isGestureActive.value = withTiming(0);
            }
            else {
                isGestureActive.value = 1;
            }
        },
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    useAppState(memoizedStateCallbacks);
    React.useEffect(() => {
        const unsubscribe = navigation.addListener('beforeRemove', () => {
            isGestureActive.value = 0;
        });
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const listRef = useRef(null);
    const videosList = useMemo(() => selectSlicedVideos(route.params.learnVideos, route.params.videoIndex), [route.params.learnVideos, route.params.videoIndex]);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const eachVideoStyle = useMemo(() => [{ width }, styles.eachVideo], [styles.eachVideo, width]);
    const videoListLength = useMemo(() => videosList.length, [videosList.length]);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const enableScrollAndroid = useCallback(() => {
        if (Platform.OS === 'android') {
            timeoutRef.current = setTimeout(() => {
                scrollEnabled.value = true;
            }, 200);
        }
    }, [scrollEnabled]);
    const handleNext = useCallback((index) => () => {
        if (scrollEnabled.value) {
            scrollEnabled.value = false;
            enableScrollAndroid();
            if (index === videoListLength - 1) {
                navigation.pop();
            }
            else {
                // @ts-ignore
                listRef.current?.scrollToIndex({
                    animated: true,
                    index: index + 1,
                });
            }
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoListLength, enableScrollAndroid]);
    const handlePrev = useCallback((index) => () => {
        if (scrollEnabled.value) {
            scrollEnabled.value = false;
            enableScrollAndroid();
            if (index > 0) {
                // @ts-ignore
                listRef.current?.scrollToIndex({
                    animated: true,
                    index: index - 1,
                });
            }
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableScrollAndroid]);
    const renderItem = useCallback(({ item, index }) => (<View style={eachVideoStyle}>
        <LearnVideo index={index} title={item.name} videoUrl={item.videoUrl} handleNext={handleNext(index)} handlePrev={handlePrev(index)} isFocused={activeIndex === index} isGestureActive={isGestureActiveBool}/>
      </View>), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeIndex, eachVideoStyle, handleNext, handlePrev]);
    const getItemLayout = useCallback((_data, index) => ({
        length: width,
        offset: width * index,
        index,
    }), [width]);
    const onViewableItemsChanged = useCallback((info) => {
        const viewableItemsLength = info.viewableItems?.length;
        if (Platform.OS === 'ios' ? viewableItemsLength === 1 : viewableItemsLength) {
            const { index } = info.viewableItems[viewableItemsLength - 1];
            if (index !== null) {
                setActiveIndex(index);
            }
        }
    }, []);
    const handleScroll = useAnimatedScrollHandler({
        onBeginDrag: () => {
            isGestureActive.value = 1;
        },
        onEndDrag: (event) => {
            if (event.contentOffset.x % width < 0.1) {
                isGestureActive.value = withTiming(0, { duration: 100 });
            }
        },
        onMomentumEnd: () => {
            isGestureActive.value = withTiming(0, { duration: 100 });
            scrollEnabled.value = true;
        },
    }, []);
    const animatedProps = useAnimatedProps(() => ({
        scrollEnabled: scrollEnabled.value,
    }), []);
    return (<AppView type="primary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={[styles.container, { paddingTop: top, paddingBottom: bottom }]}>
        <Animated.FlatList horizontal ref={listRef} pagingEnabled windowSize={5} bounces={false} data={videosList} initialNumToRender={3} onScroll={handleScroll} renderItem={renderItem} decelerationRate="fast" disableIntervalMomentum maxToRenderPerBatch={3} scrollEventThrottle={16} onMomentumScrollEnd={noop} getItemLayout={getItemLayout} {...(Platform.OS === 'android' && { viewabilityConfig })} showsHorizontalScrollIndicator={false} keyExtractor={extractKeyFromKey('name')} onViewableItemsChanged={onViewableItemsChanged} animatedProps={animatedProps}/>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
        backgroundColor: colors.background.black,
    },
    eachVideo: {
        flex: 1,
        overflow: 'hidden',
    },
}));
export default InvestLearn;
